 
import React, { PureComponent, Children } from 'react';
import cx from 'classnames';
import { throttle, noop } from 'lodash';
import {
  Scrollable,
  calculateNextOffsetToLeft,
  calculateNextOffsetToRight,
  snapToThreshold,
  shiftThresholdToLeft,
  shiftThresholdToRight,
} from './scrollable';
import CarouselItem from './CarouselItem';

import './Carousel.scss';


const addFadeMargin = offset => Math.max(offset - 16, 0);

const shouldAlignToChild = (newOffset, outerWidth, innerWidth) =>
  !(newOffset + outerWidth >= innerWidth);

export default class Carousel extends PureComponent {

  static defaultProps = {
    activeElement: 0,
    onRightArrowClick: noop,
    onLeftArrowClick: noop,
  };

  state = {
    hidden: true,
    overflow: 'none',
  };

  componentDidMount() {
    // Hide on first render to prevent flickering with SSR
     
    this.setState({
      hidden: false,
    });
    window.addEventListener('resize', this._setOverflowStateCallback);
    this.intervalId = setInterval(this._setOverflowStateCallback, 300);
    this._convertChildrenMapToOrderedArray();
    this._setOverflowState();
    this._scrollToActive({ transition: false });
  }

  componentDidUpdate({ activeElement, children }) {
    if (this.props.activeElement !== activeElement) this._scrollToActive({ transition: true });
    if (this.props.children !== children) this._convertChildrenMapToOrderedArray();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._setOverflowStateCallback);
    clearInterval(this.intervalId);
  }

  _onLeftArrowClick = event => {
    this.props.onLeftArrowClick(event);

    if (!this._scrollable) return;
    const { scrollLeft, outerWidth, innerWidth } = this._scrollable;

    const thresholds = this._childrenNodes.map(child => child.offsetLeft);
    const shiftRight = offset => shiftThresholdToRight(offset, innerWidth, outerWidth, thresholds);
    const nextOffset = calculateNextOffsetToLeft(scrollLeft, outerWidth, innerWidth);

    if (nextOffset === 0) {
      this._scrollable.scrollTo(addFadeMargin(snapToThreshold(nextOffset, thresholds)));
    } else {
      this._scrollable.scrollTo(
        addFadeMargin(shiftRight(shiftRight(snapToThreshold(nextOffset, thresholds)))),
      );
    }
  };

  _onRightArrowClick = event => {
    this.props.onRightArrowClick(event);

    if (!this._scrollable) return;
    const { scrollLeft, outerWidth, innerWidth } = this._scrollable;
    const nextOffset = calculateNextOffsetToRight(scrollLeft, outerWidth, innerWidth);

    if (shouldAlignToChild(nextOffset, outerWidth, innerWidth)) {
      const thresholds = this._childrenNodes.map(child => child.offsetLeft);
      this._scrollable.scrollTo(addFadeMargin(snapToThreshold(nextOffset, thresholds)));
    } else {
      this._scrollable.scrollTo(nextOffset);
    }
  };

  _makeChildrenRefGetter = idx => el => {
    if (el) {
      this._childrenNodesMap.set(idx, el);
    } else {
      this._childrenNodesMap.delete(idx);
    }
  };

  _getScrollableRef = el => {
    this._scrollable = el;
  };

  _setOverflowState = () => {
    if (this._scrollable) {
      const { overflow } = this._scrollable;
      if (this.state.overflow !== overflow) {
        this.setState({ overflow });
      }
    }
  };

  _setOverflowStateCallback = throttle(this._setOverflowState, 100, {
    trailing: true,
    leading: true,
  });

  _scrollToActive = ({ transition }) => {
    if (!this._scrollable) return;
    const { activeElement } = this.props;
    const activeElementOffset = this._childrenNodesMap.get(activeElement)
      ? this._childrenNodesMap.get(activeElement).offsetLeft
      : 0;
    const offsets = this._childrenNodes.map(child => child.offsetLeft);
    const nextOffset = addFadeMargin(
      shiftThresholdToLeft(
        snapToThreshold(activeElementOffset, offsets),
        this._scrollable.innerWidth,
        this._scrollable.outerWidth,
        offsets,
      ),
    );
    if (transition) {
      this._scrollable.scrollTo(nextOffset);
    } else {
      this._scrollable.scrollLeft = nextOffset;
    }
  };

  _convertChildrenMapToOrderedArray = () => {
    this._childrenNodes = Array.from(this._childrenNodesMap.entries())
      .sort(([elementOneIndex], [elementTwoIndex]) => elementOneIndex - elementTwoIndex)
      .map(entry => entry[1]);
  };

  _childrenNodes = null;
  _childrenNodesMap = new Map();
  _scrollable = null;

  render() {
    const { overflow, hidden } = this.state;
    const {
      children: carouselChildren,
      loadingElement,
    } = this.props;

    const carouselContainerClassName = cx('sb-header__carousel__container', {
      containerMask: overflow === 'both',
      containerMaskLeft: overflow === 'left',
      containerMaskRight: overflow === 'right',
    });

    return (
      <>
        <div className={carouselContainerClassName}>
          <Scrollable ref={this._getScrollableRef} onScroll={this._setOverflowStateCallback}>
            {Children.map(carouselChildren, (child, idx) => (
              <CarouselItem
                innerRef={this._makeChildrenRefGetter(idx)}
                loading={loadingElement === idx}
                key={idx}
                data-test-id={`Carousel.item-${idx}`}
              >
                {child}
              </CarouselItem>
            ))}
          </Scrollable>
        </div>
      </>
    );
  };
}
